// The venue table as an adapter.
//
// `synthetic`: there is no network here. `fetch` serializes the checked-in
// table into bytes and hands them over, which sounds like ceremony and is not
// — those bytes get content-hashed and stored exactly like nflverse's, so an
// unchanged table writes nothing, a changed one shows up as a diff against a
// stored capture, and the coordinates have a history rather than a git blame.
//
// It asserts no entities. The venues already exist, minted from the schedule
// with an `nflverse-stadium` key; this source only says where they are. That is
// why every fact here references a venue it did not create, which is the case
// the platform had never had to resolve before Phase 6.

import type {
  ExtractResult,
  FetchContext,
  FetchItem,
  FetchPlan,
  ProbeResult,
  SourceAdapter,
  StoredCapture,
} from '../../acquisition/adapter';
import { venues as manifest } from './manifest';
import { VENUES, type VenueRecord } from './data';

export const LOCATIONS = 'locations';
/** Every resource this adapter fetches, for the credits (`resourcesRead`). */
export const VENUES_READS: readonly string[] = [LOCATIONS];

/** nflverse's stadium namespace. The venues are theirs; the coordinates are ours. */
export const VENUE_NS = 'nflverse-stadium';

/**
 * A version token for the table.
 *
 * Derived from the content so that editing a coordinate changes it and
 * re-editing it back changes it back. A hand-maintained version number would
 * be one more thing to forget.
 */
export function tableToken(records: readonly VenueRecord[]): string {
  let h = 2166136261;
  const text = JSON.stringify(records);
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `venues-${records.length}-${(h >>> 0).toString(16)}`;
}

export function extractVenues(text: string, observedAt: string): ExtractResult {
  const parsed: unknown = JSON.parse(text);
  if (!Array.isArray(parsed)) throw new Error('venues: capture is not an array');

  const facts = [];
  for (const row of parsed as VenueRecord[]) {
    facts.push({
      kind: 'venue.location',
      // The grain: one row per venue, forever. A corrected coordinate
      // supersedes rather than sitting beside the old one.
      key: row.id,
      payload: {
        venueId: row.id,
        name: row.name,
        latitude: row.latitude,
        longitude: row.longitude,
        city: row.city,
        region: row.region,
        country: row.country,
        nwsCovered: row.nwsCovered,
      },
      validFrom: observedAt,
      validTo: null,
      refs: [{ namespace: VENUE_NS, key: row.id, role: 'subject' }],
      topics: [],
      confidence: 'reported' as const,
    });
  }

  return {
    entities: [],
    facts,
    unmapped: [],
    // Deliberately complete: this table is the whole of what we claim to know
    // about venue locations, so a venue dropped from it has genuinely stopped
    // being one we hold a coordinate for.
    complete: [{ kind: 'venue.location', prefix: '' }],
    skipped: [],
  };
}

export function venuesAdapter(records: readonly VenueRecord[] = VENUES): SourceAdapter {
  const token = tableToken(records);
  const body = new TextEncoder().encode(JSON.stringify(records, null, 1));

  return {
    manifest,

    plan(ctx): FetchPlan {
      return {
        items: [{ resource: LOCATIONS, url: `venues://table/${token}` }],
        because: `${records.length} curated venues, as of ${ctx.now}`,
      };
    },

    // No network, so the table always knows its own version — and because the
    // token is derived from the content, an unedited table probes as unchanged
    // and never re-serializes.
    probe(): Promise<ProbeResult> {
      return Promise.resolve({ changed: true, token });
    },

    fetch(item: FetchItem, ctx: FetchContext): Promise<StoredCapture> {
      if (item.resource !== LOCATIONS) {
        throw new Error(`venues: no such resource "${item.resource}"`);
      }
      return Promise.resolve({ id: '', resource: item.resource, fetchedAt: ctx.now, body });
    },

    extract(capture: StoredCapture): ExtractResult {
      return extractVenues(new TextDecoder('utf-8').decode(capture.body), capture.fetchedAt);
    },
  };
}

export const venuesSourceAdapter = venuesAdapter();
